// src/pages/Lounge/MemberFriendsFeed/MemberFriendsFeed.jsx
import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link, useLocation, useOutletContext } from "react-router-dom";
import { useTranslation } from "react-i18next";

import TextPostCard from "@/components/Post/TextPostCard/TextPostCard";
import PhotoPostCard from "@/components/Post/PhotoPostCard/PhotoPostCard";
import VideoPostCard from "@/components/Post/VideoPostCard/VideoPostCard";
import Loading from "@/components/Loading/Loading";

import api from "@/utils/axios";

import cls from "./MemberFriendsFeed.module.scss";

import avatarPng from "@/assets/UI/avatar.png";

const LANGS = ["en", "si", "ta", "hi"];
const FEED_LIMIT = 15;

const safeStr = (v) => (v == null ? "" : String(v));
const hasText = (v) => safeStr(v).trim().length > 0;

const extFromFile = (file) => {
  const name = safeStr(file?.name);
  const dot = name.lastIndexOf(".");
  if (dot === -1) return "";
  return name.slice(dot + 1).toLowerCase();
};

const getVideoIdFromPost = (post) => {
  const v = post?.videoId;
  if (!v) return "";
  if (typeof v === "string") return v;
  if (typeof v === "object" && v?._id) return v._id;
  return "";
};

/** Same helper idea as VideoUpload.jsx: read intrinsic dimensions before presign */
async function sniffVideoMeta(file) {
  return new Promise((resolve) => {
    try {
      const el = document.createElement("video");
      el.preload = "metadata";
      el.muted = true;
      el.playsInline = true;

      el.onloadedmetadata = () => {
        URL.revokeObjectURL(el.src);
        const width = el.videoWidth || 0;
        const height = el.videoHeight || 0;
        const orientation =
          width && height && width < height ? "portrait" : "landscape";
        resolve({ width, height, orientation });
      };

      el.onerror = () => {
        try {
          URL.revokeObjectURL(el.src || "");
        } catch {
          // Intentionally ignored: this best-effort cleanup should not block the UI.
        }
        resolve({ width: 0, height: 0, orientation: "landscape" });
      };

      el.src = URL.createObjectURL(file);
    } catch {
      resolve({ width: 0, height: 0, orientation: "landscape" });
    }
  });
}

const MemberFriendsFeed = () => {
  const { t } = useTranslation();
  const location = useLocation();

  const outlet = useOutletContext() || {};
  const avatarSrc = outlet.avatarSrc || avatarPng;

  const isPublic = location.pathname === "/lounge";
  const isFriends = location.pathname === "/lounge/friends-feed";
  const isClubs = location.pathname === "/lounge/clubs-feed";
  const isChat = location.pathname === "/lounge/chat";
  const isSearch = location.pathname === "/lounge/search";

  // feed
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [feedErr, setFeedErr] = useState("");
  const [posts, setPosts] = useState([]);
  const [hasMore, setHasMore] = useState(true);
  const [nextPage, setNextPage] = useState(2);
  const loadMoreRef = useRef(null);
  const pagingRequestRef = useRef(false);

  // upload progress
  const [uploadPct, setUploadPct] = useState(0);
  const [uploadLabel, setUploadLabel] = useState("");

  // composer
  const [composerOpen, setComposerOpen] = useState(false);
  const [mode, setMode] = useState(""); // "" | "text" | "photo" | "video"

  const [lang, setLang] = useState("en");
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");

  const [images, setImages] = useState([]); // File[]
  const [videoFile, setVideoFile] = useState(null); // File|null

  // preview-only
  const [imagePreviewUrls, setImagePreviewUrls] = useState([]);
  const [videoPreviewUrl, setVideoPreviewUrl] = useState("");

  const [submitting, setSubmitting] = useState(false);
  const [formErr, setFormErr] = useState("");
  const [formOk, setFormOk] = useState("");

  const loadFeed = async ({ page = 1, append = false } = {}) => {
    if (append && pagingRequestRef.current) return;
    if (append) pagingRequestRef.current = true;

    if (append) {
      setLoadingMore(true);
    } else {
      setLoading(true);
    }

    setFeedErr("");
    try {
      const res = await api.get("/v1/posts/feed/friends", {
        params: { page, limit: FEED_LIMIT },
      });

      const rows =
        res?.data?.rows ||
        res?.data?.posts ||
        res?.data?.data ||
        res?.data?.items ||
        [];

      const normalizedRows = Array.isArray(rows) ? rows : [];
      const hasMoreFromApi = Boolean(res?.data?.meta?.hasMore);

      setPosts((prev) =>
        append ? [...prev, ...normalizedRows] : normalizedRows,
      );
      setHasMore(hasMoreFromApi || normalizedRows.length === FEED_LIMIT);
      setNextPage(page + 1);
    } catch (e) {
      setFeedErr(
        e?.response?.data?.message || e?.message || "Failed to load feed",
      );
      if (!append) {
        setPosts([]);
      }
    } finally {
      if (append) pagingRequestRef.current = false;
      if (append) {
        setLoadingMore(false);
      } else {
        setLoading(false);
      }
    }
  };

  useEffect(() => {
    loadFeed({ page: 1, append: false });
  }, []);

  useEffect(() => {
    if (!hasMore || loading || loadingMore) return undefined;
    if (typeof window === "undefined") return undefined;
    if (!("IntersectionObserver" in window)) return undefined;

    const observer = new IntersectionObserver(
      (entries) => {
        if (!entries[0]?.isIntersecting || pagingRequestRef.current) return;
        loadFeed({ page: nextPage, append: true });
      },
      { rootMargin: "200px 0px" },
    );

    const el = loadMoreRef.current;
    if (el) observer.observe(el);

    return () => observer.disconnect();
  }, [hasMore, loading, loadingMore, nextPage]);

  // cleanup previews
  useEffect(() => {
    return () => {
      try {
        imagePreviewUrls.forEach((u) => u && URL.revokeObjectURL(u));
      } catch {
        // Intentionally ignored: this best-effort cleanup should not block the UI.
      }
      try {
        if (videoPreviewUrl) URL.revokeObjectURL(videoPreviewUrl);
      } catch {
        // Intentionally ignored: this best-effort cleanup should not block the UI.
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const clearPreviews = () => {
    setImagePreviewUrls((prev) => {
      prev.forEach((u) => {
        try {
          if (u) URL.revokeObjectURL(u);
        } catch {
          // Intentionally ignored: this best-effort cleanup should not block the UI.
        }
      });
      return [];
    });

    setVideoPreviewUrl((prev) => {
      try {
        if (prev) URL.revokeObjectURL(prev);
      } catch {
        // Intentionally ignored: this best-effort cleanup should not block the UI.
      }
      return "";
    });
  };

  const resetForm = () => {
    setLang("en");
    setTitle("");
    setDescription("");
    setImages([]);
    setVideoFile(null);
    setFormErr("");
    setFormOk("");
    setUploadPct(0);
    setUploadLabel("");
    clearPreviews();
  };

  const closeComposer = () => {
    setComposerOpen(false);
    setMode("");
    resetForm();
  };

  const openComposer = (nextMode) => {
    setComposerOpen(true);
    setMode(nextMode);
    setFormErr("");
    setFormOk("");
    setImages([]);
    setVideoFile(null);
    setUploadPct(0);
    setUploadLabel("");
    clearPreviews();
  };

  const onPickImages = (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;

    const remain = Math.max(0, 20 - images.length);
    const toAdd = files.slice(0, remain);

    setImages((prev) => [...prev, ...toAdd].slice(0, 20));
    setImagePreviewUrls((prev) =>
      [...prev, ...toAdd.map((f) => URL.createObjectURL(f))].slice(0, 20),
    );

    e.target.value = "";
  };

  const removeImageAt = (idx) => {
    setImages((prev) => prev.filter((_, i) => i !== idx));

    setImagePreviewUrls((prev) => {
      const url = prev[idx];
      try {
        if (url) URL.revokeObjectURL(url);
      } catch {
        // Intentionally ignored: this best-effort cleanup should not block the UI.
      }
      return prev.filter((_, i) => i !== idx);
    });
  };

  const onPickVideo = (e) => {
    const file = e.target.files?.[0] || null;

    setVideoPreviewUrl((prev) => {
      try {
        if (prev) URL.revokeObjectURL(prev);
      } catch {
        // Intentionally ignored: this best-effort cleanup should not block the UI.
      }
      return file ? URL.createObjectURL(file) : "";
    });

    setVideoFile(file);
    e.target.value = "";
  };

  const validateClient = () => {
    setFormErr("");
    setFormOk("");

    if (!LANGS.includes(lang)) {
      setFormErr(t("memberPublicFeed.create.errors.lang"));
      return false;
    }

    if (mode === "text") {
      const okText = hasText(title) || hasText(description);
      if (!okText) {
        setFormErr(t("memberPublicFeed.create.errors.textRequired"));
        return false;
      }
    }

    if (mode === "photo") {
      if (images.length < 1) {
        setFormErr(t("memberPublicFeed.create.errors.photoRequired"));
        return false;
      }
      if (images.length > 20) {
        setFormErr(t("memberPublicFeed.create.errors.photoMax"));
        return false;
      }
    }

    if (mode === "video") {
      if (!videoFile) {
        setFormErr(t("memberPublicFeed.create.errors.videoRequired"));
        return false;
      }
    }

    return true;
  };

  const presignImagePoster = async (file) => {
    const ext = extFromFile(file) || "jpg";
    const contentType = file?.type || "image/jpeg";
    const res = await api.post("/v1/posts/media/presign-image-poster", {
      ext,
      contentType,
    });
    return res.data;
  };

  /**
   * ✅ Same as VideoUpload.jsx:
   * 1) sniff clientMeta
   * 2) POST /v1/videos/presign
   */
  const presignVideoViaVideos = async (file) => {
    const contentType = file?.type || "application/octet-stream";
    const filename =
      safeStr(file?.name) || `video.${extFromFile(file) || "mp4"}`;

    const clientMeta = (await sniffVideoMeta(file).catch(() => null)) || {
      width: 0,
      height: 0,
      orientation: "landscape",
    };

    const { data } = await api.post("/v1/videos/presign", {
      contentType,
      filename,
      clientMeta,
    });

    return data; // expects: { id, jobId, url, contentType, ... }
  };

  const putToS3WithProgress = (uploadUrl, file, onPct, contentTypeOverride) => {
    return new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest();
      xhr.open("PUT", uploadUrl);

      xhr.setRequestHeader(
        "Content-Type",
        contentTypeOverride || file?.type || "application/octet-stream",
      );

      xhr.upload.onprogress = (evt) => {
        if (!evt.lengthComputable) return;
        const pct = Math.round((evt.loaded / evt.total) * 100);
        onPct?.(pct);
      };

      xhr.onload = () => {
        if (xhr.status >= 200 && xhr.status < 300) return resolve();
        reject(new Error(`Upload failed (${xhr.status})`));
      };

      xhr.onerror = () => reject(new Error("Upload failed (network error)"));
      xhr.send(file);
    });
  };

  const buildVideoMetaForLocalize = () => {
    const blank = {
      en: { title: "", subtitle: "" },
      si: { title: "", subtitle: "" },
      ta: { title: "", subtitle: "" },
      hi: { title: "", subtitle: "" },
    };

    blank[lang] = {
      title: hasText(title) ? title.trim() : "",
      subtitle: hasText(description) ? description.trim() : "",
    };

    return blank;
  };

  const submitPost = async () => {
    if (!validateClient()) return;

    setSubmitting(true);
    setFormErr("");
    setFormOk("");
    setUploadPct(0);
    setUploadLabel("");

    try {
      const baseBody = {
        // ✅ difference: friends-only
        visibility: "friends-only",
        postLanguage: lang,
        title: hasText(title) ? title.trim() : null,
        description: hasText(description) ? description.trim() : null,
        publishMode: "now",
      };

      if (mode === "text") {
        setUploadLabel(t("memberPublicFeed.create.publishing"));
        await api.post("/v1/posts", { ...baseBody, type: "text-only" });
      }

      if (mode === "photo") {
        const uploaded = [];

        for (let i = 0; i < images.length; i++) {
          const f = images[i];

          setUploadLabel(
            t("memberPublicFeed.create.uploadingPhoto", {
              current: i + 1,
              total: images.length,
            }),
          );
          setUploadPct(0);

          const pres = await presignImagePoster(f);
          await putToS3WithProgress(pres.uploadUrl, f, setUploadPct);

          uploaded.push({ key: pres.processedKey });
        }

        setUploadLabel(t("memberPublicFeed.create.publishing"));
        setUploadPct(100);

        await api.post("/v1/posts", {
          ...baseBody,
          type: "photo-post",
          images: uploaded,
        });
      }

      if (mode === "video") {
        setUploadLabel(t("memberPublicFeed.create.uploadingVideo"));
        setUploadPct(0);

        const presign = await presignVideoViaVideos(videoFile);

        const videoId = safeStr(presign?.id).trim();
        if (!videoId) {
          console.log("videos/presign response:", presign);
          throw new Error("Server did not return a video id for this upload.");
        }

        await putToS3WithProgress(
          presign.url,
          videoFile,
          setUploadPct,
          presign.contentType || videoFile?.type,
        );

        const meta = buildVideoMetaForLocalize();
        await api.post(`/v1/videos/${videoId}/localize`, { meta });

        await api.post(`/v1/videos/${videoId}/submit`);

        setUploadLabel(t("memberPublicFeed.create.publishing"));
        setUploadPct(100);

        await api.post("/v1/posts", {
          ...baseBody,
          type: "video-post",
          videoId,
        });
      }

      setFormOk(t("memberPublicFeed.create.success"));
      resetForm();
      await loadFeed({ page: 1, append: false });
      closeComposer();
    } catch (e) {
      setFormErr(e?.response?.data?.message || e?.message || "Failed");
    } finally {
      setSubmitting(false);
      setUploadLabel("");
      setUploadPct(0);
    }
  };

  const publishBtnText = useMemo(() => {
    if (!submitting) return t("memberPublicFeed.create.publish");
    if (!uploadLabel) return t("memberPublicFeed.create.publishing");
    if (uploadPct > 0 && uploadPct < 100)
      return `${uploadLabel} (${uploadPct}%)`;
    if (uploadPct === 100) return `${uploadLabel} (100%)`;
    return uploadLabel;
  }, [submitting, uploadLabel, uploadPct, t]);

  const renderFeedPost = (post) => {
    const type = safeStr(post?.type).toLowerCase();
    const hasImages = Array.isArray(post?.images) && post.images.length > 0;
    const hasVideo = !!getVideoIdFromPost(post);

    if (type === "video-post" || type === "video")
      return <VideoPostCard post={post} />;
    if (type === "photo-post" || type === "photo")
      return <PhotoPostCard post={post} />;
    if (type === "text-only" || type === "text")
      return <TextPostCard post={post} />;

    if (hasVideo) return <VideoPostCard post={post} />;
    if (hasImages) return <PhotoPostCard post={post} />;
    return <TextPostCard post={post} />;
  };

  // ✅ Loader gate AFTER hooks
  if (loading) {
    return (
      <Loading
        fullscreen
        titleKey="loading.title"
        subtitleKey="loading.subtitle"
        showTips
        cycleMs={1400}
      />
    );
  }

  return (
    <div className={cls.page}>
      <div className={cls.pageInner}>
        <nav className={cls.tabs}>
          <Link
            to="/lounge"
            className={`${cls.tabButton} ${isPublic ? cls.activeTab : ""}`}
          >
            {t("memberPublicFeed.tabs.public")}
          </Link>
          <Link
            to="/lounge/friends-feed"
            className={`${cls.tabButton} ${isFriends ? cls.activeTab : ""}`}
          >
            {t("memberPublicFeed.tabs.friends")}
          </Link>
          <Link
            to="/lounge/clubs-feed"
            className={`${cls.tabButton} ${isClubs ? cls.activeTab : ""}`}
          >
            {t("memberPublicFeed.tabs.clubs")}
          </Link>
          <Link
            to="/lounge/chat"
            className={`${cls.tabButton} ${isChat ? cls.activeTab : ""}`}
          >
            {t("memberPublicFeed.tabs.chat")}
          </Link>
          <Link
            to="/lounge/search"
            className={`${cls.tabButton} ${isSearch ? cls.activeTab : ""}`}
          >
            🔍 {t("memberPublicFeed.tabs.search")}
          </Link>
        </nav>

        <div className={cls.layout}>
          <div className={cls.rightColumn}>
            <section className={cls.card}>
              <div className={cls.createHeader}>
                <img
                  src={avatarSrc}
                  alt={t("memberPublicFeed.avatarAlt")}
                  className={cls.createPostAvatar}
                />
                <div className={cls.createHeaderText}>
                  <div className={cls.createTitle}>
                    {t("memberFriendsFeed.create.title")}
                  </div>
                  <div className={cls.createSub}>
                    {t("memberFriendsFeed.create.sub")}
                  </div>
                </div>
              </div>

              <div className={cls.modeRow}>
                <button
                  type="button"
                  className={`${cls.modeBtn} ${
                    mode === "text" ? cls.modeBtnActive : ""
                  }`}
                  onClick={() => openComposer("text")}
                  disabled={submitting}
                >
                  ✍️ {t("memberPublicFeed.create.modes.text")}
                </button>

                <button
                  type="button"
                  className={`${cls.modeBtn} ${
                    mode === "photo" ? cls.modeBtnActive : ""
                  }`}
                  onClick={() => openComposer("photo")}
                  disabled={submitting}
                >
                  🖼️ {t("memberPublicFeed.create.modes.photo")}
                </button>

                <button
                  type="button"
                  className={`${cls.modeBtn} ${
                    mode === "video" ? cls.modeBtnActive : ""
                  }`}
                  onClick={() => openComposer("video")}
                  disabled={submitting}
                >
                  🎥 {t("memberPublicFeed.create.modes.video")}
                </button>
              </div>

              {composerOpen ? (
                <div className={cls.form}>
                  <div className={cls.formRow}>
                    <label className={cls.label}>
                      {t("memberPublicFeed.create.language")}
                    </label>
                    <select
                      className={cls.select}
                      value={lang}
                      onChange={(e) => setLang(e.target.value)}
                      disabled={submitting}
                    >
                      {LANGS.map((l) => (
                        <option key={l} value={l}>
                          {t(`memberPublicFeed.create.langs.${l}`)}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div className={cls.formRow}>
                    <label className={cls.label}>
                      {t("memberPublicFeed.create.titleLabel")}
                    </label>
                    <input
                      className={cls.input}
                      value={title}
                      onChange={(e) => setTitle(e.target.value)}
                      placeholder={t("memberPublicFeed.create.titlePh")}
                      maxLength={160}
                      disabled={submitting}
                    />
                  </div>

                  <div className={cls.formRow}>
                    <label className={cls.label}>
                      {t("memberPublicFeed.create.descLabel")}
                    </label>
                    <textarea
                      className={cls.textarea}
                      value={description}
                      onChange={(e) => setDescription(e.target.value)}
                      placeholder={t("memberPublicFeed.create.descPh")}
                      maxLength={4000}
                      rows={4}
                      disabled={submitting}
                    />
                  </div>

                  {mode === "photo" ? (
                    <div className={cls.formRow}>
                      <div className={cls.uploadRow}>
                        <div className={cls.uploadMeta}>
                          <div className={cls.uploadTitle}>
                            {t("memberPublicFeed.create.photosTitle")}
                          </div>
                          <div className={cls.uploadHint}>
                            {t("memberPublicFeed.create.photosHint")}
                          </div>
                        </div>

                        <label className={cls.fileBtn}>
                          {t("memberPublicFeed.create.addPhotos")}
                          <input
                            type="file"
                            accept="image/*"
                            multiple
                            onChange={onPickImages}
                            disabled={submitting || images.length >= 20}
                            className={cls.fileInput}
                          />
                        </label>
                      </div>

                      {imagePreviewUrls.length ? (
                        <div className={cls.previewGrid}>
                          {imagePreviewUrls.map((url, idx) => (
                            <div key={url} className={cls.previewItem}>
                              <img
                                src={url}
                                alt={t(
                                  "memberPublicFeed.create.previewPhotoAlt",
                                )}
                                className={cls.previewImg}
                              />
                              <button
                                type="button"
                                className={cls.previewRemove}
                                onClick={() => removeImageAt(idx)}
                                disabled={submitting}
                                aria-label="remove"
                              >
                                ✕
                              </button>
                            </div>
                          ))}
                        </div>
                      ) : null}
                    </div>
                  ) : null}

                  {mode === "video" ? (
                    <div className={cls.formRow}>
                      <div className={cls.uploadRow}>
                        <div className={cls.uploadMeta}>
                          <div className={cls.uploadTitle}>
                            {t("memberPublicFeed.create.videoTitle")}
                          </div>
                          <div className={cls.uploadHint}>
                            {t("memberPublicFeed.create.videoHint")}
                          </div>
                        </div>

                        <label className={cls.fileBtn}>
                          {t("memberPublicFeed.create.addVideo")}
                          <input
                            type="file"
                            accept="video/*"
                            onChange={onPickVideo}
                            disabled={submitting}
                            className={cls.fileInput}
                          />
                        </label>
                      </div>

                      {videoFile ? (
                        <div className={cls.videoPicked}>
                          <span
                            className={cls.videoName}
                            title={videoFile.name}
                          >
                            {videoFile.name}
                          </span>
                          <button
                            type="button"
                            className={cls.thumbRemove}
                            onClick={() => {
                              setVideoFile(null);
                              setVideoPreviewUrl((prev) => {
                                try {
                                  if (prev) URL.revokeObjectURL(prev);
                                } catch {
                                  // Intentionally ignored: this best-effort cleanup should not block the UI.
                                }
                                return "";
                              });
                            }}
                            disabled={submitting}
                          >
                            ✕
                          </button>
                        </div>
                      ) : null}

                      {videoPreviewUrl ? (
                        <div className={cls.videoPreview}>
                          <video
                            className={cls.videoEl}
                            src={videoPreviewUrl}
                            controls
                            playsInline
                            preload="metadata"
                          />
                        </div>
                      ) : null}
                    </div>
                  ) : null}

                  {formErr ? (
                    <div className={cls.formErr}>{formErr}</div>
                  ) : null}
                  {formOk ? <div className={cls.formOk}>{formOk}</div> : null}

                  <div className={cls.actionsRow}>
                    <button
                      type="button"
                      className={cls.publishBtn}
                      onClick={submitPost}
                      disabled={submitting}
                    >
                      {publishBtnText}
                    </button>

                    <button
                      type="button"
                      className={cls.resetBtn}
                      onClick={resetForm}
                      disabled={submitting}
                    >
                      {t("memberPublicFeed.create.reset")}
                    </button>

                    <button
                      type="button"
                      className={cls.cancelBtn}
                      onClick={closeComposer}
                      disabled={submitting}
                    >
                      {t("memberPublicFeed.create.cancel")}
                    </button>
                  </div>

                  {mode === "video" ? (
                    <div className={cls.noteMini}>
                      {t("memberPublicFeed.create.videoNote")}
                    </div>
                  ) : null}
                </div>
              ) : null}
            </section>

            <section className={cls.feedWrap}>
              {feedErr ? (
                <div className={cls.feedStateBad}>{feedErr}</div>
              ) : posts.length === 0 ? (
                <div className={cls.feedState}>
                  {t("memberFriendsFeed.feed.empty")}
                </div>
              ) : (
                <div className={cls.postList}>
                  {posts.map((post) => (
                    <React.Fragment key={post._id}>
                      {renderFeedPost(post)}
                    </React.Fragment>
                  ))}
                  {hasMore ? (
                    <div ref={loadMoreRef} className={cls.feedLoadState}>
                      {loadingMore
                        ? "Loading more posts..."
                        : "Scroll to load more posts"}
                    </div>
                  ) : (
                    <div className={cls.feedLoadState}>No more posts</div>
                  )}
                </div>
              )}
            </section>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MemberFriendsFeed;
